import { useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Mail, Phone, Wallet, Sparkles, CheckCircle2, ArrowRight, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignupQuick() {
  const [sp] = useSearchParams();
  const [step, setStep] = useState(1);            // 1 = email, 2 = otp+details
  const [role, setRole] = useState(sp.get("role") === "assistant" ? "assistant" : "customer");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [upiId, setUpiId] = useState("");
  const [otp, setOtp] = useState("");
  const [devHint, setDevHint] = useState("");     // shows dev OTP hint from backend (mocked mode)
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const sendOtp = async (e) => {
    e?.preventDefault?.();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Enter a valid email");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/auth/quick-signup/send-otp`, { email });
      if (r.data?.dev_otp) {
        setDevHint(`DEV MODE — your OTP is ${r.data.dev_otp}`);
      }
      toast.success("OTP sent to your email — check inbox");
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send OTP");
    } finally {
      setSending(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    if (!otp || otp.length < 4) { toast.error("Enter the OTP"); return; }
    if (!/^\+?\d[\d\s-]{6,}$/.test(mobile)) { toast.error("Enter a valid mobile number"); return; }
    if (!upiId || upiId.length < 3 || !upiId.includes("@")) { toast.error("Enter a valid UPI id (e.g. name@upi)"); return; }
    setVerifying(true);
    try {
      const r = await axios.post(`${API}/auth/quick-signup/verify`, {
        email, otp, mobile, upi_id: upiId, name, role,
      });
      localStorage.setItem("hastaan_token", r.data.token);
      toast.success("Welcome to HeylpMate! Complete your profile any time.");
      // Force AuthContext to re-read the token by reloading
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not verify OTP");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-[80vh] py-10 md:py-16 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="label-eyebrow">30-second signup</span>
          <h1 className="mt-3 font-display text-4xl md:text-5xl font-bold" style={{ color: "#00604A" }}>
            Get in fast. <span className="italic" style={{ color: "#F45118" }}>Verify later.</span>
          </h1>
          <p className="mt-3 text-sm md:text-base max-w-xl mx-auto" style={{ color: "#667085" }}>
            Just email, mobile & UPI — no selfie or Aadhaar upfront. We'll ping you to finish KYC when you're ready.
          </p>
        </div>

        {/* Role toggle */}
        <div className="flex justify-center mb-6" data-testid="quick-role-toggle">
          <div className="inline-flex rounded-full p-1 border" style={{ borderColor: "#00604A22" }}>
            <button data-testid="quick-role-customer"
              onClick={() => setRole("customer")}
              className={`px-4 py-2 text-sm font-bold rounded-full transition ${role === "customer" ? "text-white" : ""}`}
              style={role === "customer" ? { background: "#F45118" } : { color: "#00604A" }}>
              I want help
            </button>
            <button data-testid="quick-role-assistant"
              onClick={() => setRole("assistant")}
              className={`px-4 py-2 text-sm font-bold rounded-full transition ${role === "assistant" ? "text-white" : ""}`}
              style={role === "assistant" ? { background: "#00604A" } : { color: "#00604A" }}>
              I want to help
            </button>
          </div>
        </div>

        <div className="rounded-3xl border bg-white p-6 md:p-10 shadow-sm" style={{ borderColor: "#E4E7E5" }}>
          {step === 1 && (
            <form onSubmit={sendOtp} className="space-y-5" data-testid="quick-step-email">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#667085" }}>Email</span>
                <div className="mt-2 flex items-center gap-3 border-2 rounded-2xl px-4 py-3" style={{ borderColor: "#E4E7E5" }}>
                  <Mail className="w-5 h-5" style={{ color: "#00604A" }} />
                  <input
                    data-testid="quick-email-input"
                    type="email" required autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="w-full outline-none bg-transparent text-base"
                  />
                </div>
              </label>
              <button data-testid="quick-send-otp-btn"
                type="submit" disabled={sending}
                className="w-full py-3 rounded-full font-bold text-white shadow-md hover:opacity-95 transition inline-flex items-center justify-center gap-2"
                style={{ background: "#F45118" }}>
                {sending ? "Sending..." : (<>Send OTP <ArrowRight className="w-4 h-4" /></>)}
              </button>
              <div className="text-center text-xs" style={{ color: "#667085" }}>
                Already have an account? <Link to="/login" className="font-bold underline" style={{ color: "#00604A" }}>Log in</Link>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verify} className="space-y-5" data-testid="quick-step-verify">
              {devHint && (
                <div data-testid="quick-dev-hint"
                  className="text-xs px-3 py-2 rounded-lg font-mono"
                  style={{ background: "#FEF3EA", color: "#F45118", border: "1px dashed #F45118" }}>
                  {devHint}
                </div>
              )}
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#667085" }}>6-digit OTP</span>
                <input
                  data-testid="quick-otp-input"
                  type="text" inputMode="numeric" maxLength={6} required autoFocus
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  className="mt-2 w-full border-2 rounded-2xl px-4 py-3 outline-none text-center text-2xl tracking-[0.5em] font-bold"
                  style={{ borderColor: "#E4E7E5", color: "#00604A" }}
                  placeholder="••••••"
                />
              </label>

              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#667085" }}>Full name (optional)</span>
                <input
                  data-testid="quick-name-input"
                  value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="How should we call you?"
                  className="mt-2 w-full border-2 rounded-2xl px-4 py-3 outline-none"
                  style={{ borderColor: "#E4E7E5" }}
                />
              </label>

              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#667085" }}>Mobile number *</span>
                <div className="mt-2 flex items-center gap-3 border-2 rounded-2xl px-4 py-3" style={{ borderColor: "#E4E7E5" }}>
                  <Phone className="w-5 h-5" style={{ color: "#00604A" }} />
                  <input
                    data-testid="quick-mobile-input"
                    type="tel" required
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="+91 98xxxxxxxx"
                    className="w-full outline-none bg-transparent"
                  />
                </div>
              </label>

              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#667085" }}>UPI ID *</span>
                <div className="mt-2 flex items-center gap-3 border-2 rounded-2xl px-4 py-3" style={{ borderColor: "#E4E7E5" }}>
                  <Wallet className="w-5 h-5" style={{ color: "#00604A" }} />
                  <input
                    data-testid="quick-upi-input"
                    required
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    placeholder="yourname@upi"
                    className="w-full outline-none bg-transparent"
                  />
                </div>
                <span className="text-[11px] mt-1 block" style={{ color: "#667085" }}>
                  We'll use this to pay you (or refund you) via UPI.
                </span>
              </label>

              <div className="rounded-2xl p-3 flex items-start gap-2 text-xs"
                   style={{ background: "#FFF8F1", border: "1px solid #F4511822", color: "#8A4A22" }}>
                <ShieldAlert className="w-4 h-4 flex-none mt-0.5" style={{ color: "#F45118" }} />
                <div>
                  Your profile is created as <b>pending admin verification</b>. You can still browse & book —
                  KYC (Aadhaar / selfie) can be completed later from your profile.
                </div>
              </div>

              <button data-testid="quick-verify-btn"
                type="submit" disabled={verifying}
                className="w-full py-3 rounded-full font-bold text-white shadow-md hover:opacity-95 transition inline-flex items-center justify-center gap-2"
                style={{ background: "#00604A" }}>
                {verifying ? "Creating account..." : (<><CheckCircle2 className="w-4 h-4" /> Create account & continue</>)}
              </button>

              <button type="button" onClick={() => setStep(1)}
                data-testid="quick-change-email"
                className="w-full text-center text-xs font-bold underline"
                style={{ color: "#667085" }}>
                Change email
              </button>
            </form>
          )}
        </div>

        <div className="mt-6 text-center text-xs flex flex-wrap items-center justify-center gap-3" style={{ color: "#667085" }}>
          <Sparkles className="w-3.5 h-3.5" style={{ color: "#F45118" }} />
          <span>Zero documents upfront</span>
          <span>·</span>
          <span>Verify at your own pace</span>
          <span>·</span>
          <span>UPI-first payouts</span>
        </div>
      </div>
    </div>
  );
}
